import { useMemo } from 'react';

import { useCreateClient as useCreateClientApi } from 'api/requests';
import { ErrorCode } from 'api/types/error';
import { isFinanceProject, isGlobalProject } from 'config';
import { useServerErrorNotify } from 'hooks';
import { FormSubmit } from 'libs/form/Form';
import { useTranslation } from 'libs/i18n';
import { ROUTES, useNavigate } from 'libs/navigation';
import { useNotify } from 'libs/notify';

import { CreateClientFormType, FormErrors } from '../forms/createClientForm';

export const useCreateClient = () => {
  const notify = useNotify();
  const { t } = useTranslation();
  const { navigate } = useNavigate();
  const isFinance = isFinanceProject();
  const isGlobal = isGlobalProject();

  const {
    createClient,
    error: submitError,
    loading: submitLoading,
  } = useCreateClientApi();

  const formError = useMemo((): FormErrors | undefined => {
    if (submitError?.code === ErrorCode.USER_WITH_EMAIL_ALREADY_EXISTS) {
      return { owner: { email: submitError?.message } };
    }

    return undefined;
  }, [submitError?.code, submitError?.message]);

  const onSubmit: FormSubmit<CreateClientFormType> = async (values) => {
    const client = await createClient({
      email: values.owner.email,
      firstName: values.owner.firstName,
      lastName: values.owner.lastName,
      middleName: values.owner.middleName,
      phoneNumber: values.owner.phoneNumber,
      dateOfBirth: values.owner.dateOfBirth,
      personalAccountNumber: values.owner.personalAccountNumber,
      personalAccountOpeningDate: values.owner.personalAccountOpeningDate,
      companySignedAgreementFileId_Finance: isFinance
        ? values.companySignedClientServiceAgreementFiles[0].id
        : undefined,
      companySignedClientServiceAgreementFileId_Global: isGlobal
        ? values.companySignedClientServiceAgreementFiles[0].id
        : undefined,
      companySignedNoticeOfInvestmentAccountOpeningFileId:
        values.companySignedNoticeOfInvestmentAccountOpeningFiles[0].id!,
      brightCode: values.owner.brightCode,
      representative: values.representative.missingRepresentative
        ? undefined
        : {
            email: values.representative.email!,
            firstName: values.representative.firstName!,
            lastName: values.representative.lastName!,
            middleName: values.representative.middleName,
            phoneNumber: values.representative.phoneNumber!,
            dateOfBirth: values.representative.dateOfBirth!,
          },
    });

    if (client) {
      notify.info(t('client.createClient.successMessage'));
      navigate(ROUTES.clients.personClients.personClient.fullPath, {
        clientId: client.id,
      });
    }
  };

  useServerErrorNotify(submitError);

  return { onSubmit, formError, submitLoading };
};
